type SearchParamsReader = {
  get: (name: string) => string | null;
};

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
};


export type ChannelType = "paid" | "organic" | "partner" | "direct";

const UTM_COOKIE_NAME = "utm_params";
const UTM_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;


const UTM_SOURCE_WHITELIST = new Set([
  "meta", "facebook", "instagram", "tiktok", "twitter", "linkedin",
  "whatsapp", "intelli", "brevo", "mailerlite", "blog", "techcabal",
  "affiliate", "bella-solomon", "influencer", "google", "direct", "referral",
  "bing", "yahoo", "duckduckgo", "baidu",
]);

const UTM_MEDIUM_WHITELIST = new Set([
  "cpc", "organic-social", "email", "whatsapp", "content",
  "sponsored", "paid-search", "paid-social", "paid-influencer",
  "affiliate", "influencer", "referral", "organic-search",
]);


const PAID_MEDIUMS = new Set([
  "cpc", "sponsored", "paid-search", "paid-social", "paid-influencer",
]);
const ORGANIC_MEDIUMS = new Set([
  "organic-social", "content", "email", "whatsapp", "referral", "organic-search",
]);
const PARTNER_MEDIUMS = new Set(["affiliate", "influencer"]);


function normalizeUtmValue(value: string | undefined | null): string | undefined {
  if (value == null || typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase().replace(/_/g, "-");
  return normalized === "" ? undefined : normalized;
}

function compactUtmParams(params: UtmParams): UtmParams {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => typeof value === "string" && value.trim() !== ""),
  ) as UtmParams;
}


export interface UtmValidationResult {
  value: string | undefined;
  isValid: boolean;
}


export function validateUtmSource(source?: string): UtmValidationResult {
  const normalized = normalizeUtmValue(source);
  if (!normalized) return { value: undefined, isValid: true };
  const isValid = UTM_SOURCE_WHITELIST.has(normalized);
  if (!isValid) {
    console.warn(`[UTM] Unclassified utm_source: "${source}" → normalized: "${normalized}". Expected one of: ${[...UTM_SOURCE_WHITELIST].join(", ")}`);
  }
  return { value: normalized, isValid };
}

export function validateUtmMedium(medium?: string): UtmValidationResult {
  const normalized = normalizeUtmValue(medium);
  if (!normalized) return { value: undefined, isValid: true };
  const isValid = UTM_MEDIUM_WHITELIST.has(normalized);
  if (!isValid) {
    console.warn(`[UTM] Unclassified utm_medium: "${medium}" → normalized: "${normalized}". Expected one of: ${[...UTM_MEDIUM_WHITELIST].join(", ")}`);
  }
  return { value: normalized, isValid };
}


export function getChannelType(
  utmMedium?: string | null,
  clickIds?: Pick<UtmParams, "gclid" | "fbclid" | "ttclid">,
): ChannelType {
  if (!utmMedium?.trim()) {
    return clickIds?.gclid || clickIds?.fbclid || clickIds?.ttclid
      ? "paid"
      : "direct";
  }
  const normalized = utmMedium.trim().toLowerCase().replace(/_/g, "-");
  if (PAID_MEDIUMS.has(normalized)) return "paid";
  if (PARTNER_MEDIUMS.has(normalized)) return "partner";
  if (ORGANIC_MEDIUMS.has(normalized)) return "organic";
  return "direct";
}


export function getUtmParamsFromSearchParams(searchParams: SearchParamsReader): UtmParams {
  const raw: UtmParams = {
    utm_source: normalizeUtmValue(searchParams.get("utm_source")),
    utm_medium: normalizeUtmValue(searchParams.get("utm_medium")),
    utm_campaign: normalizeUtmValue(searchParams.get("utm_campaign")),
    utm_content: normalizeUtmValue(searchParams.get("utm_content")),
    utm_term: normalizeUtmValue(searchParams.get("utm_term")),
    // Click IDs are opaque tokens — preserve original casing
    gclid: searchParams.get("gclid") || undefined,
    fbclid: searchParams.get("fbclid") || undefined,
    ttclid: searchParams.get("ttclid") || undefined,
  };

  // Validate against whitelists (logs warnings for unknown values)
  if (raw.utm_source) validateUtmSource(raw.utm_source);
  if (raw.utm_medium) validateUtmMedium(raw.utm_medium);

  return compactUtmParams(raw);
}

export function getStoredUtmParams(): UtmParams {
  if (typeof document === "undefined") return {};

  const rawCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${UTM_COOKIE_NAME}=`))
    ?.split("=")[1];

  if (!rawCookie) return {};

  try {
    return compactUtmParams(JSON.parse(decodeURIComponent(rawCookie)) as UtmParams);
  } catch {
    return {};
  }
}

export function persistUtmParams(params: UtmParams) {
  if (typeof document === "undefined") return;

 
  const normalizedParams: UtmParams = {
    utm_source: normalizeUtmValue(params.utm_source),
    utm_medium: normalizeUtmValue(params.utm_medium),
    utm_campaign: normalizeUtmValue(params.utm_campaign),
    utm_content: normalizeUtmValue(params.utm_content),
    utm_term: normalizeUtmValue(params.utm_term),
  
    gclid: params.gclid || undefined,
    fbclid: params.fbclid || undefined,
    ttclid: params.ttclid || undefined,
  };

  const compactParams = compactUtmParams(normalizedParams);
  if (!Object.keys(compactParams).length) return;


  if (compactParams.utm_source) validateUtmSource(compactParams.utm_source);
  if (compactParams.utm_medium) validateUtmMedium(compactParams.utm_medium);

  document.cookie = [
    `${UTM_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(compactParams))}`,
    "path=/",
    `max-age=${UTM_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ].join("; ");
}

export function getCurrentOrStoredUtmParams(searchParams: SearchParamsReader): UtmParams {
  const urlParams = getUtmParamsFromSearchParams(searchParams);
  if (Object.keys(urlParams).length) {
    persistUtmParams(urlParams);
    return urlParams;
  }

  const storedParams = getStoredUtmParams();
  if (Object.keys(storedParams).length) return storedParams;

  const derived = deriveSourceFromReferrer();
  if (derived.utm_medium === "(none)") return {};

  const derivedParams: UtmParams = {
    utm_source: derived.utm_source,
    utm_medium: derived.utm_medium,
  };
  persistUtmParams(derivedParams);
  return derivedParams;
}


export function isInternalReferrer(): boolean {
  if (typeof document === "undefined" || typeof window === "undefined") return false;
  if (!document.referrer) return false;
  try {
    const referrerUrl = new URL(document.referrer);
    return referrerUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}


export function deriveSourceFromReferrer(): { utm_source: string; utm_medium: string } {
  if (typeof document === "undefined") {
    return { utm_source: "direct", utm_medium: "(none)" };
  }

  const referrer = document.referrer;
  if (!referrer) {
    return { utm_source: "direct", utm_medium: "(none)" };
  }

  try {
    const referrerUrl = new URL(referrer);
    const hostname = referrerUrl.hostname.toLowerCase();

    if (hostname.includes("google.")) return { utm_source: "google", utm_medium: "organic-search" };
    if (hostname.includes("bing.")) return { utm_source: "bing", utm_medium: "organic-search" };
    if (hostname.includes("yahoo.")) return { utm_source: "yahoo", utm_medium: "organic-search" };
    if (hostname.includes("duckduckgo.")) return { utm_source: "duckduckgo", utm_medium: "organic-search" };
    if (hostname.includes("baidu.")) return { utm_source: "baidu", utm_medium: "organic-search" };

  
    if (typeof window !== "undefined" && referrerUrl.origin === window.location.origin) {
      return { utm_source: "direct", utm_medium: "(none)" };
    }


    return { utm_source: hostname.replace(/^www\./, ""), utm_medium: "referral" };
  } catch {
    return { utm_source: "direct", utm_medium: "(none)" };
  }
}

export function parseUtmCookie(cookieValue?: string | null): UtmParams {
  if (!cookieValue) return {};
  try {
    return compactUtmParams(JSON.parse(decodeURIComponent(cookieValue)) as UtmParams);
  } catch {
    return {};
  }
}
